import { Button, Spinner } from "flowbite-react"
import { FormikProps } from "formik"
import React, { Dispatch, useCallback, useRef, useState } from "react"
import useSWR from "swr"

import DangerouslyHTMLTextPriview from "@components/elements/DangerouslyHTMLTextPriview"
import MonoImageGallery from "@components/elements/MonoImageGallery/MonoImageGallery"
import MonoSelect from "@components/elements/Select/MonoSelect"
import useToast from "@hooks/useToast"
import RightArrow from "@icons/RightArrow"
import * as ProductServices from "@mono/essentials/services/ProductServices"
import { IDomain } from "@mono/essentials/types/UserTypes"

import AddGiftCardInfo from "./AddGiftCardInfo"

interface viewDetailsProps {
  handleNextStep: (arg0: any, arg1: number) => void
  selectedProduct: string | undefined
  isIntegratedPage: boolean
  setProductInfo?: Dispatch<React.SetStateAction<any | undefined>>
  domainDetails: IDomain
  selectedProductData?: any
  isSelectedProductDetailValidating?: any
  formInitialValues?: any
}

const GiftCardViewDetails = ({
  handleNextStep,
  selectedProduct,
  isIntegratedPage,
  setProductInfo,
  domainDetails,
  selectedProductData,
  isSelectedProductDetailValidating,
  formInitialValues,
}: viewDetailsProps) => {
  const { showToast } = useToast()
  const [unitCount, setUnitCount] = useState<number>(1)
  const participantDetailsRef = useRef<FormikProps<any>>(null)
  const selectedProductDetail = selectedProductData ?? {}

  const { data: getValidityData = [], isValidating: isGetValidityDataValidating } = useSWR(
    selectedProduct ? `productPriceData-${selectedProduct}` : null,
    async () => {
      const { data } = await ProductServices.getGiftCardValidity(
        selectedProduct,
        {},
        isIntegratedPage
      )
      return data?.data
    },
    {
      revalidateOnFocus: false,
    }
  )

  const images = selectedProductDetail?.mediaFiles?.map((image: any) => {
    return {
      original: `${image.public_url}`,
      thumbnail: `${image.public_url}`,
    }
  })

  const handleProductPrice = (value: number) => {
    setUnitCount((prev) => prev + value)
  }

  const handleCounter = useCallback(() => {
    return (
      <>
        <div>
          <div className={"flex justify-between items-center"}>
            <div className={"flex items-center gap-4"}>
              <div className={"text14Medium text-neutral-900"}>Qty.</div>
              {domainDetails?.quantity_indicator === "increment_decrement_button" ? (
                <div>
                  <div className={"ml-1 flex items-center gap-2.5"}>
                    <div>
                      <button
                        type={"button"}
                        tabIndex={-1}
                        disabled={unitCount === 0}
                        onClick={() => handleProductPrice(-1)}
                      >
                        <span
                          className={`m-auto block h-8 w-8 text-primary-600 rounded-md bg-primary-300 text-xl`}
                        >
                          −
                        </span>
                      </button>
                    </div>
                    <div
                      className={
                        "flex h-8 w-8 items-center justify-center border rounded-md border-neutral-450 px-1 py-1.5"
                      }
                    >
                      <div className={"text15Medium"}>{unitCount}</div>
                    </div>
                    <div>
                      <button type={"button"} tabIndex={-1} onClick={() => handleProductPrice(1)}>
                        <span
                          className={
                            "m-auto text-primary-600 block h-[30px] w-[30px] rounded-md bg-primary-300 text-xl"
                          }
                        >
                          +
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <MonoSelect
                  value={{ label: unitCount, value: unitCount }}
                  options={Array.from({ length: 10 }, (_, index) => ({
                    value: index + 1,
                    label: `${index + 1}`,
                  }))}
                  onValueChange={(newValue) => {
                    setUnitCount(newValue?.value)
                  }}
                  menuPosition={"fixed"}
                  className={"!w-44"}
                  name={"select_unit_count"}
                />
              )}
            </div>
          </div>
        </div>
      </>
    )
  }, [unitCount])

  const handleSubmit = () => {
    participantDetailsRef?.current?.submitForm()
  }

  if (isGetValidityDataValidating && isSelectedProductDetailValidating) {
    return (
      <div className={"text-center"}>
        <Spinner aria-label={"Center-aligned"} />
      </div>
    )
  }

  return (
    <div>
      <div>
        <div className={"p-2 sm:p-0"}>
          <div>
            {images?.length > 0 && (
              <MonoImageGallery images={images || []} ContainerClassName={"w-full"} />
            )}
          </div>
          {getValidityData?.gift_card_type === "specific_products" && (
            <div
              className={`bg-primary-200 border-primary-400 border-t border-b p-3 paragraph16Regular my-4`}
            >
              <div className={"flex justify-between items-center"}>
                <div className={"text14Regular text-neutral-700"}>
                  Amount:{" "}
                  <span
                    className={"text-neutral-1000 text14Semibold"}
                  >{`${getValidityData?.amount_options?.[0]?.amount_formatted}`}</span>
                </div>
                <div className={"text14Regular text-neutral-700"}>
                  * Apply Only on Specific Product:{" "}
                  <span className={"text-neutral-900 text14Semibold"}>
                    {getValidityData?.products?.map((product) => product.name).join(", ")}
                  </span>
                </div>
              </div>
            </div>
          )}
          <div className={"text20Semibold text-neutral-1000 mt-3"}>Details</div>
          <DangerouslyHTMLTextPriview
            content={selectedProductDetail?.description?.toString() ?? ""}
            className={"paragraph14Medium text-neutral-600"}
          />
        </div>
      </div>
      <div className={"mt-5 pb-3 border-b border-neutral-200"}>
        <div className={"flex justify-start items-center gap-3"}>
          <div className={"sm:text16Semibold text14Semibold"}>Gift Card Info</div>
          <div className={"border w-0.5 border-neutral-300 h-6"} />
          <div className={"flex items-center gap-2"}>
            <div className={"text14Regular text-neutral-700"}>Expiry Date:</div>
            <div className={"text14Semibold text-neutral-900"}>
              {getValidityData?.validity?.expiry_value || "Never"}
            </div>
          </div>
          <div className={"border w-0.5 border-neutral-300 h-6"} />
          {handleCounter()}
        </div>
      </div>
      <AddGiftCardInfo
        onSuccess={(values) => {
          handleNextStep(values, unitCount)
        }}
        selectedProduct={selectedProduct}
        isIntegratedPage={isIntegratedPage}
        units={unitCount}
        participantDetailsRef={participantDetailsRef}
        productValidityData={getValidityData}
        formInitialValues={formInitialValues}
      />

      <div className={"float-right py-5"}>
        <Button
          color={"primary"}
          className={"w-full [&>span]:!text16Medium [&>span]:!py-[13px] !h-10"}
          onClick={handleSubmit}
          data-testid={"nextButton"}
        >
          Next
          <RightArrow className={"ml-2.5 text-whiteBlack-100"} width={20} height={20} />
        </Button>
      </div>
    </div>
  )
}

export default GiftCardViewDetails
